import { ProtectedRouteContainer as ProtectedRoute } from "auth/containers/ProtectedRouteContainer";
import { SettingsContainer } from "auth/containers/SettingsContainer";
import AcceptInvite from "components/AcceptInvite/containers/AcceptInvite";
import { AdminGraph } from "components/AdminGraph";
import { UsersContainer as UserAnalytics } from "components/AppAnalytics/Users/UsersContainer";
import AuthorizeComponent from "components/Authorize";
import { LinkAWSID } from "components/Authorize/components/LinkAWSID";
import { Join, Login } from "components/Login";
import Onboarding, {
    IntegrationSuccess,
    OnboardingSignUpContainer,
    IntegrateConnectContainer,
} from "components/Onboarding";
import { OnboardingV3Container } from "components/Onboarding/containers/V3/OnboardingV3Container";
import { SetupContainer } from "components/Onboarding/containers/SetupContainer";
import Pricing from "components/Pricing/components/Pricing";
import PricingLivestream from "components/Pricing/components/PricingLivestream";
import PricingPodcast from "components/Pricing/components/PricingPodcast";
import VerifyConfirmPage from "components/Verification/containers/VerificationConfirmationPage";
import VerifyPage from "components/Verification/containers/VerificationPage";
import { NotificationPreferencesContainer } from "components/NotificationPreferences";
import { History } from "history";
import TermsPage from "pages/TermsPage";
import React, { FC } from "react";
import DocumentTitle from "react-document-title";
import { Route, Switch, Redirect } from "react-router-dom";
import { Store } from "redux";
import { State } from "redux/state";
import { Admin } from "./Admin";
import { OrganizationRoot } from "./Organization";

import { AppLayout, BareLayout } from "layouts";
import { StudioConfig } from "models/Config";
import { OnboardingLayout, RedirectingLayout } from "@xapp/components";
import { ChatSnippet } from "components/ChatSnippet";
import { HelpChat } from "components/HelpChat";
import { SignUp } from "components/Login/components/SignUp";
import { MagicLinkSignInContainer } from "components/Login/containers/MagicLinkSignInContainer";

import style from "./style.scss";

export interface RoutesProps {
    store: Store<State>;
    history: History;
    config: StudioConfig;
}

/**
 * Main top level routes for Studio
 *
 * @link - Help from how to write a router with multiple layouts from https://codesandbox.io/s/react-router-v5-multiple-layout-d5yfw?file=/src/router/index.js
 * @param props
 * @returns
 */
export const Routes: FC<RoutesProps> = (props: RoutesProps): JSX.Element => {
    const { config, history, store } = props;

    return (
        <DocumentTitle title="XAPP">
            <>
                <Switch>
                    <Route path={["/help/chat"]}>
                        <BareLayout>
                            <Switch>
                                <Route path="/help/chat" exact component={HelpChat} />
                            </Switch>
                        </BareLayout>
                    </Route>
                    <Route path={["/join", "/login", "/authorize", "/signup"]}>
                        <RedirectingLayout>
                            <Switch>
                                {/* These normal routes are unprotected meaning they don't require a login */}
                                <Redirect from="/signup" to="/join" />
                                <Route path="/join" component={Join} />
                                <Route path="/login" component={Login} key="Login" />
                                {/* The /authorize receives the tokens back from the login & join screens */}
                                <Route path="/authorize" component={AuthorizeComponent} />
                            </Switch>
                        </RedirectingLayout>
                    </Route>
                    <Route path={["/sign-up", "/sign-in-link"]}>
                        <OnboardingLayout className={style.onboardingWrapper}>
                            <Switch>
                                <Route path="/sign-up" component={SignUp} />
                                {/* Passwordless sign-in. Its own route because
                                    /login redirects to the Cognito hosted UI,
                                    which cannot host a custom auth flow. */}
                                <Route path="/sign-in-link" component={MagicLinkSignInContainer} />
                            </Switch>
                        </OnboardingLayout>
                    </Route>
                    <Route path={["/onboarding/:path?"]}>
                        <OnboardingLayout className={style.onboardingWrapper}>
                            <Switch>
                                {/* For testing old onboarding */}
                                <Route path="/onboarding/v2" exact component={Onboarding} />
                                <Route path="/onboarding" exact component={OnboardingV3Container} />
                                {/* This path is used when we connect to a third party */}
                                {/* We need to update this to use the new onboarding */}
                                <ProtectedRoute
                                    path="/onboarding/integrations/:integration/:status"
                                    exact
                                    component={Onboarding}
                                />
                                <Redirect from="/onboarding/v3" to="/onboarding" />
                                {/* Old legacy AWS Market Place onboarding link, it links the account */}
                                <ProtectedRoute path="/onboarding/aws" exact component={LinkAWSID} />
                                {/* Not protected because they don't have an account yet! */}
                                <Route path={"/onboarding/sign-up"} exact component={OnboardingSignUpContainer} />
                            </Switch>
                            <ChatSnippet
                                production={true}
                                url={"https://widget.xapp.ai"}
                                widgetKey={window?.xaStudioConfig?.widgetKey}
                            />
                        </OnboardingLayout>
                    </Route>
                    <Route path={["/setup/:appId"]}>
                        <OnboardingLayout className={style.onboardingWrapper}>
                            <Switch>
                                {/* Post-payment setup flow for specific appId */}
                                <ProtectedRoute path="/setup/:appId" component={SetupContainer} />
                            </Switch>
                        </OnboardingLayout>
                    </Route>
                    <Route path={["/integration/connect/:integrationId?", "/integration/success/:integrationId?"]}>
                        <OnboardingLayout className={style.onboardingWrapper}>
                            <Switch>
                                <ProtectedRoute
                                    path="/integration/connect/:integrationId?"
                                    component={IntegrateConnectContainer}
                                />
                                <ProtectedRoute
                                    path="/integration/success/:integrationId?"
                                    component={IntegrationSuccess}
                                />
                            </Switch>
                        </OnboardingLayout>
                    </Route>
                    <Route>
                        {" "}
                        {/* No path here so it falls through to these */}
                        <AppLayout config={config}>
                            <Switch>
                                {/** Update and remove these */}
                                <Route path="/pricing/" component={Pricing} />
                                <Route path="/podcast-pricing/" component={PricingPodcast} />
                                <Route path="/radio-pricing/" component={PricingLivestream} />
                                <Route path="/terms" component={TermsPage} />

                                {/* These are protected */}
                                <ProtectedRoute path="/accept" component={AcceptInvite} />
                                <ProtectedRoute path="/graphql" component={AdminGraph} />
                                <ProtectedRoute path="/admin" role="stentor_admin" component={Admin(history, store)} />
                                <ProtectedRoute path="/settings" exact component={SettingsContainer} />
                                <ProtectedRoute path="/settings/notifications" component={NotificationPreferencesContainer} />
                                <ProtectedRoute path="/analytics" role="stentor_admin" component={UserAnalytics} />
                                <ProtectedRoute
                                    path="/verify/confirm/callback"
                                    exact
                                    ignoreVerified={true}
                                    component={VerifyConfirmPage}
                                />
                                {/* This would be called because the user is now being verified*/}
                                <ProtectedRoute path="/verify" exact component={VerifyPage} />
                                {/* Fall through to the organization root, it attempts to find your organization ID */}
                                <ProtectedRoute path="/:ordId?/:appId?" component={OrganizationRoot(history, store)} />
                            </Switch>
                            {/* It conflicts with other widgets currently <ChatSnippet production={true} widgetKey={window.xaStudioConfig.widgetKey} /> */}
                        </AppLayout>
                    </Route>
                </Switch>
            </>
        </DocumentTitle>
    );
};

export default Routes;
